import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Snackbar,
  Autocomplete,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { api } from '../../services/apiClient';
import { procurementService, VendorPayment, Vendor } from '../../services/procurementService';

interface TDSCalculation {
  tdsSection: string;
  tdsRate: number;
  tdsAmount: number;
  netAmount: number;
  thresholdApplied: boolean;
  cumulativePayments: number;
  message?: string;
}

interface PurchaseInvoice {
  id: string;
  invoice_no: string;
  total_amount: number;
  paid_amount: number;
  balance_amount: number;
}

const VendorPaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<VendorPayment[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Summary stats
  const [stats, setStats] = useState({
    totalPayments: 0,
    pending: 0,
    processed: 0,
    completed: 0,
    totalAmount: 0,
    totalTDS: 0,
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'view'>('create');
  const [selectedPayment, setSelectedPayment] = useState<VendorPayment | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    vendor_id: '',
    invoice_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    gross_amount: 0,
    tds_section: '',
    tds_rate: 0,
    tds_amount: 0,
    net_amount: 0,
    payment_mode: 'BANK_TRANSFER' as VendorPayment['payment_mode'],
    bank_reference: '',
    notes: '',
  });

  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [tdsCalculating, setTdsCalculating] = useState(false);
  const [tdsInfo, setTdsInfo] = useState<TDSCalculation | null>(null);
  const tdsRequestRef = useRef(0);
  const vendorIdRef = useRef('');
  const grossAmountRef = useRef(0);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const paymentModes = [
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
    { value: 'NEFT', label: 'NEFT' },
    { value: 'RTGS', label: 'RTGS' },
    { value: 'CHEQUE', label: 'Cheque' },
    { value: 'UPI', label: 'UPI' },
    { value: 'CASH', label: 'Cash' },
  ];

  useEffect(() => {
    fetchPayments();
    fetchVendors();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await procurementService.getVendorPayments();
      setPayments(response.payments || []);
      setStats(response.summary || {
        totalPayments: 0,
        pending: 0,
        processed: 0,
        completed: 0,
        totalAmount: 0,
        totalTDS: 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await procurementService.getVendors();
      setVendors(response.vendors.filter((v: Vendor) => v.is_active));
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  };

  const fetchVendorInvoices = async (vendorId: string) => {
    try {
      const response = await api.get<{ invoices: PurchaseInvoice[] }>(`/procurement/purchase-invoices`, {
        params: { vendorId, status: 'APPROVED' }
      });
      setInvoices(response.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setInvoices([]);
    }
  };

  const calculateTDS = async (vendorId: string, amount: number) => {
    if (!vendorId || amount <= 0) {
      setTdsInfo(null);
      return;
    }

    const requestId = ++tdsRequestRef.current;

    try {
      setTdsCalculating(true);
      const response = await api.post<{ calculation: TDSCalculation }>('/procurement/calculate-tds', {
        vendorId,
        amount,
      });

      // Ignore stale responses from previous requests
      if (requestId !== tdsRequestRef.current) return;

      const calc = response.calculation;
      setTdsInfo(calc);

      // Validate and apply TDS values
      const tdsAmount = calc.tdsAmount || 0;
      const validTds = tdsAmount >= 0 && tdsAmount <= amount ? tdsAmount : 0;

      setFormData(prev => ({
        ...prev,
        tds_section: calc.tdsSection || '',
        tds_rate: calc.tdsRate || 0,
        tds_amount: validTds,
        net_amount: prev.gross_amount - validTds,
      }));
    } catch (err) {
      console.error('TDS calculation failed:', err);
      if (requestId !== tdsRequestRef.current) return;
      setFormData(prev => ({
        ...prev,
        tds_section: '',
        tds_rate: 0,
        tds_amount: 0,
        net_amount: prev.gross_amount,
      }));
    } finally {
      if (requestId === tdsRequestRef.current) {
        setTdsCalculating(false);
      }
    }
  };

  const handleVendorChange = async (vendor: Vendor | null) => {
    setSelectedVendor(vendor);
    if (vendor) {
      vendorIdRef.current = vendor.id;
      setFormData(prev => ({ ...prev, vendor_id: vendor.id, invoice_id: '' }));
      await fetchVendorInvoices(vendor.id);
      if (grossAmountRef.current > 0) {
        await calculateTDS(vendor.id, grossAmountRef.current);
      }
    } else {
      vendorIdRef.current = '';
      setFormData(prev => ({ ...prev, vendor_id: '', invoice_id: '' }));
      setInvoices([]);
      setTdsInfo(null);
    }
  };

  const handleAmountChange = async (amount: number) => {
    grossAmountRef.current = amount;
    setFormData(prev => ({ ...prev, gross_amount: amount }));
    if (vendorIdRef.current && amount > 0) {
      await calculateTDS(vendorIdRef.current, amount);
    } else {
      setFormData(prev => ({ ...prev, tds_amount: 0, net_amount: amount }));
      setTdsInfo(null);
    }
  };

  const handleInvoiceSelect = (invoiceId: string) => {
    const invoice = invoices.find(i => i.id === invoiceId);
    if (invoice) {
      const amount = invoice.balance_amount || invoice.total_amount;
      grossAmountRef.current = amount;
      setFormData(prev => ({ ...prev, invoice_id: invoiceId, gross_amount: amount }));
      if (vendorIdRef.current) {
        calculateTDS(vendorIdRef.current, amount);
      }
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.vendor_id) {
      errors.vendor_id = 'Vendor is required';
    }
    if (!formData.payment_date) {
      errors.payment_date = 'Payment date is required';
    }
    if (formData.gross_amount <= 0) {
      errors.gross_amount = 'Amount must be greater than zero';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // Backend expects camelCase
      const payload = {
        vendorId: formData.vendor_id,
        invoiceId: formData.invoice_id || undefined,
        paymentDate: formData.payment_date,
        grossAmount: formData.gross_amount,
        tdsSection: formData.tds_section || undefined,
        tdsRate: formData.tds_rate || 0,
        tdsAmount: formData.tds_amount || 0,
        netAmount: formData.net_amount || formData.gross_amount,
        paymentMode: formData.payment_mode,
        bankReference: formData.bank_reference || undefined,
        notes: formData.notes || undefined,
      };

      await procurementService.createVendorPayment(payload);
      setSnackbar({ open: true, message: 'Payment created successfully', severity: 'success' });
      setOpenDialog(false);
      resetForm();
      await fetchPayments();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create payment';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const resetForm = () => {
    vendorIdRef.current = '';
    grossAmountRef.current = 0;
    tdsRequestRef.current = 0;
    setFormData({
      vendor_id: '',
      invoice_id: '',
      payment_date: new Date().toISOString().split('T')[0],
      gross_amount: 0,
      tds_section: '',
      tds_rate: 0,
      tds_amount: 0,
      net_amount: 0,
      payment_mode: 'BANK_TRANSFER',
      bank_reference: '',
      notes: '',
    });
    setSelectedVendor(null);
    setInvoices([]);
    setTdsInfo(null);
    setFormErrors({});
  };

  const handleCreate = () => {
    navigate('/procurement/payments/new');
  };

  const handleView = (payment: VendorPayment) => {
    setDialogMode('view');
    setSelectedPayment(payment);
    setFormData({
      vendor_id: payment.vendor_id,
      invoice_id: payment.invoice_id || '',
      payment_date: payment.payment_date?.split('T')[0] || '',
      gross_amount: payment.gross_amount,
      tds_section: payment.tds_section || '',
      tds_rate: payment.tds_rate || 0,
      tds_amount: payment.tds_amount,
      net_amount: payment.net_amount,
      payment_mode: payment.payment_mode,
      bank_reference: payment.bank_reference || '',
      notes: payment.notes || '',
    });
    setOpenDialog(true);
  };

  const handleProcessPayment = async (payment: VendorPayment) => {
    try {
      await procurementService.processVendorPayment(payment.id);
      setSnackbar({ open: true, message: 'Payment processed successfully', severity: 'success' });
      await fetchPayments();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to process payment', severity: 'error' });
    }
  };

  const handleCompletePayment = async (payment: VendorPayment) => {
    try {
      await procurementService.completeVendorPayment(payment.id);
      setSnackbar({ open: true, message: 'Payment completed successfully', severity: 'success' });
      await fetchPayments();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to complete payment', severity: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PROCESSED': return 'info';
      case 'PENDING': return 'warning';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const filteredPayments = payments.filter(payment => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!payment.payment_number?.toLowerCase().includes(search) &&
          !payment.vendor_name?.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (vendorFilter && payment.vendor_id !== vendorFilter) return false;
    if (statusFilter !== 'all' && payment.status !== statusFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Vendor Payments
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Create Payment
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PaymentIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="primary.main">{stats.totalPayments}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Payments</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <WarningIcon color="warning" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="warning.main">{stats.pending}</Typography>
                  <Typography variant="body2" color="text.secondary">Pending</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BankIcon color="info" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="info.main">{stats.processed}</Typography>
                  <Typography variant="body2" color="text.secondary">Processed</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CompleteIcon color="success" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="success.main">{stats.completed}</Typography>
                  <Typography variant="body2" color="text.secondary">Completed</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <BankIcon color="secondary" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h5" color="secondary.main">
                    {formatCurrency(stats.totalAmount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Paid</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ReceiptIcon color="error" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h5" color="error.main">
                    {formatCurrency(stats.totalTDS)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">TDS Deducted</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Search Payments"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
                placeholder="Search by payment number or vendor..."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                  label="Vendor"
                >
                  <MenuItem value="">All Vendors</MenuItem>
                  {vendors.map(vendor => (
                    <MenuItem key={vendor.id} value={vendor.id}>{vendor.company_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="PROCESSED">Processed</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredPayments.length} payments
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Payment #</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell align="right">Gross Amount</TableCell>
              <TableCell align="right">TDS</TableCell>
              <TableCell align="right">Net Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPayments.map((payment) => (
              <TableRow key={payment.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">{payment.payment_number}</Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">{payment.vendor_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{payment.vendor_code}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {payment.payment_date && new Date(payment.payment_date).toLocaleDateString('en-IN')}
                </TableCell>
                <TableCell>
                  <Chip label={payment.payment_mode?.replace('_', ' ')} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right">{formatCurrency(payment.gross_amount)}</TableCell>
                <TableCell align="right">
                  {payment.tds_amount > 0 ? (
                    <Box>
                      <Typography color="error" variant="body2">-{formatCurrency(payment.tds_amount)}</Typography>
                      {payment.tds_section && (
                        <Typography variant="caption" color="text.secondary">{payment.tds_section}</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold" color="primary">{formatCurrency(payment.net_amount)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={payment.status} color={getStatusColor(payment.status) as any} size="small" />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleView(payment)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    {payment.status === 'PENDING' && (
                      <Tooltip title="Process Payment">
                        <IconButton size="small" color="info" onClick={() => handleProcessPayment(payment)}>
                          <BankIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {payment.status === 'PROCESSED' && (
                      <Tooltip title="Complete Payment">
                        <IconButton size="small" color="success" onClick={() => handleCompletePayment(payment)}>
                          <CompleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredPayments.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">No payments found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/View Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create Vendor Payment' : 'Payment Details'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                value={selectedVendor}
                onChange={(_, newValue) => handleVendorChange(newValue)}
                options={vendors}
                getOptionLabel={(option) => `${option.company_name} (${option.vendor_code})`}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">{option.company_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.vendor_code} | TDS: {option.tds_category || 'N/A'} | PAN: {option.pan || 'N/A'}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor"
                    required
                    error={!!formErrors.vendor_id}
                    helperText={formErrors.vendor_id}
                  />
                )}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                error={!!formErrors.payment_date}
                helperText={formErrors.payment_date}
                disabled={dialogMode === 'view'}
                required
              />
            </Grid>

            {invoices.length > 0 && dialogMode === 'create' && (
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Link to Invoice (Optional)</InputLabel>
                  <Select
                    value={formData.invoice_id}
                    onChange={(e) => handleInvoiceSelect(e.target.value)}
                    label="Link to Invoice (Optional)"
                  >
                    <MenuItem value="">No Invoice</MenuItem>
                    {invoices.map(invoice => (
                      <MenuItem key={invoice.id} value={invoice.id}>
                        {invoice.invoice_no} - Balance: {formatCurrency(invoice.balance_amount || invoice.total_amount)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Amount & TDS" size="small" />
              </Divider>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Gross Amount"
                type="number"
                value={formData.gross_amount}
                onChange={(e) => handleAmountChange(parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                error={!!formErrors.gross_amount}
                helperText={formErrors.gross_amount}
                disabled={dialogMode === 'view'}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="TDS Section"
                value={formData.tds_section || 'No TDS'}
                disabled
                InputProps={{
                  endAdornment: tdsCalculating ? <CircularProgress size={20} /> : null,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="TDS Rate"
                value={formData.tds_rate ? `${formData.tds_rate}%` : '0%'}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="TDS Amount"
                value={formData.tds_amount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                disabled
                sx={{ '& .MuiInputBase-input': { color: 'error.main' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Net Payable"
                value={formData.net_amount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                disabled
                sx={{ '& .MuiInputBase-input': { color: 'success.main', fontWeight: 'bold' } }}
              />
            </Grid>

            {tdsInfo && tdsInfo.message && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">{tdsInfo.message}</Alert>
              </Grid>
            )}

            {selectedVendor && !selectedVendor.pan && formData.tds_amount > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  Higher TDS rate (20%) applied as vendor does not have a valid PAN on file.
                </Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Payment Details" size="small" />
              </Divider>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Payment Mode</InputLabel>
                <Select
                  value={formData.payment_mode}
                  onChange={(e) => setFormData(prev => ({ ...prev, payment_mode: e.target.value as any }))}
                  label="Payment Mode"
                  disabled={dialogMode === 'view'}
                >
                  {paymentModes.map(mode => (
                    <MenuItem key={mode.value} value={mode.value}>{mode.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Bank Reference / UTR"
                value={formData.bank_reference}
                onChange={(e) => setFormData(prev => ({ ...prev, bank_reference: e.target.value }))}
                disabled={dialogMode === 'view'}
                placeholder="Transaction reference number"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                disabled={dialogMode === 'view'}
                multiline
                rows={2}
              />
            </Grid>

            {dialogMode === 'view' && selectedPayment && (
              <>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }}>
                    <Chip label="Vendor Bank Details" size="small" />
                  </Divider>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" color="text.secondary">Bank Name</Typography>
                  <Typography variant="body1">{selectedPayment.vendor_bank_name || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" color="text.secondary">Account Number</Typography>
                  <Typography variant="body1">{selectedPayment.vendor_bank_account || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" color="text.secondary">IFSC Code</Typography>
                  <Typography variant="body1">{selectedPayment.vendor_ifsc_code || '-'}</Typography>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            {dialogMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogMode === 'create' && (
            <Button variant="contained" onClick={handleSubmit}>
              Create Payment
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorPaymentPage;
